import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, AlertCircle, History, ChevronLeft, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import ReviewHistory from './ReviewHistory';
import { Avatar } from '../ui';
import {
  approveAssessment,
  denyAssessment,
  approveStudyDraft,
  denyStudyDraft,
} from '../../services/reviewWorkflowApi';

/**
 * Fixed bottom action bar shown while an approver is *reviewing* an in-review
 * entity inside its builder (assessment or study draft).
 *
 * Replaces the old one-click inline Approve button in the list: the approver now
 * opens the content, looks at it, and makes the decision from this bar. Approve
 * routes through a confirmation/attestation step (feeding the Part 11 e-signature
 * record); Deny requires a reason. Both return to the originating queue/list.
 *
 * Props:
 *   entityType - 'assessment' | 'study'
 *   ident      - approve/deny key: assessment uuid, or study-draft id
 *   entity     - optional entity object for display (name, submitter, date) + history id
 *   backTo     - route to return to after a decision (default per entity type)
 */
const REVIEW_API = {
  assessment: { approve: approveAssessment, deny: denyAssessment },
  study: { approve: approveStudyDraft, deny: denyStudyDraft },
};

const DEFAULT_BACK = {
  assessment: '/assessment-builder/review-queue',
  study: '/studybuilder/drafts',
};

const errorMessage = (err, fallback) =>
  err?.response?.data?.detail || err?.response?.data?.reason || fallback;

const fmtDate = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const ReviewActionBar = ({ entityType = 'assessment', ident, entity, backTo }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [denyOpen, setDenyOpen] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNote, setApproveNote] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  // The dev/uat/demo EnvironmentBanner is fixed to bottom-0 at a high z-index;
  // float this bar just above it (offset = 0 in prod where the banner is absent).
  const [bottomOffset, setBottomOffset] = useState(0);

  useEffect(() => {
    const measure = () => {
      const banner = document.querySelector('.env-dev-banner');
      setBottomOffset(banner ? banner.getBoundingClientRect().height : 0);
    };
    measure();
    // Re-measure shortly after mount (banner animates in) and on resize.
    const t = setTimeout(measure, 400);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', measure);
    };
  }, []);

  const api = REVIEW_API[entityType] || REVIEW_API.assessment;
  const back = backTo || DEFAULT_BACK[entityType] || '/';

  // Entity-aware copy. Study designs return to "Incomplete" on denial (FECOA-1604);
  // assessments return to "Draft". The mockups are the study wording.
  const isStudy = entityType === 'study';
  const noun = isStudy ? 'Design' : 'Assessment';
  const returnState = isStudy ? 'Incomplete' : 'Draft';

  const name = entity?.name || entity?.survey?.name || 'this item';
  const submitter = entity?.submitted_by_email
    || (entity?.submitted_by && typeof entity.submitted_by === 'object' ? entity.submitted_by.email : null);
  const submittedAt = fmtDate(entity?.submitted_at);

  const goBack = () => navigate(back);

  const confirmApprove = async () => {
    setBusy(true);
    try {
      await api.approve(ident, approveNote.trim());
      toast.success('Approved.');
      setApproveOpen(false);
      goBack();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not approve.'));
      setBusy(false);
    }
  };

  const confirmDeny = async () => {
    if (!denyReason.trim()) return;
    setBusy(true);
    try {
      await api.deny(ident, denyReason.trim());
      toast.success(`${noun} denied and returned to the author.`);
      setDenyOpen(false);
      goBack();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not deny.'));
      setBusy(false);
    }
  };

  if (!ident) return null;

  return (
    <>
      {/* Spacer so page content can scroll clear of the fixed bar + env banner */}
      <div aria-hidden="true" style={{ height: 80 + bottomOffset }} />

      <div
        className="fixed left-0 right-0 z-[9999] border-t border-gray-200/80 bg-white dark:border-white/[0.08] dark:bg-gray-900"
        style={{ bottom: bottomOffset }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={goBack}
              className="btn btn-ghost btn-sm flex-shrink-0"
              title="Back to review queue"
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">Back</span>
            </button>
            <div className="badge badge-dot badge-info hidden flex-shrink-0 sm:inline-flex">
              <span>Reviewing</span>
            </div>
            <div className="min-w-0">
              <div className="truncate text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">{name}</div>
              <div className="mt-0.5 flex items-center gap-3 text-[12.5px] text-gray-500 dark:text-gray-400">
                {submitter && (
                  <span className="flex min-w-0 items-center gap-1.5 truncate">
                    <Avatar name={submitter} size="xs" /> {submitter}
                  </span>
                )}
                {submittedAt && (
                  <span className="flex flex-shrink-0 items-center gap-1.5 tabular-nums">
                    <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true" /> {submittedAt}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {entity?.id != null && (
              <button
                onClick={() => setHistoryOpen(true)}
                className="btn btn-ghost"
                title="Review history"
              >
                <History className="h-4 w-4" aria-hidden="true" />
                <span className="hidden md:inline">History</span>
              </button>
            )}
            <button
              onClick={() => setDenyOpen(true)}
              disabled={busy}
              className="btn btn-secondary"
            >
              <X className="h-4 w-4" aria-hidden="true" />
              <span>Deny &amp; Return</span>
            </button>
            <button
              onClick={() => setApproveOpen(true)}
              disabled={busy}
              className="btn btn-primary"
            >
              <Check className="h-4 w-4" aria-hidden="true" />
              <span>Approve {noun}</span>
            </button>
          </div>
        </div>
      </div>

      {historyOpen && (
        <ReviewHistory
          entityType={entityType}
          objectId={entity?.id}
          entityName={name}
          currentStatus={entity?.review_status || entity?.publication_status || entity?.status}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {/* Approve confirmation / attestation */}
      {approveOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setApproveOpen(false); }}
        >
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
            <div className="flex items-start gap-2.5 border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
              <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">Approve {noun.toLowerCase()} “{name}”?</h3>
                <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
                  This records your approval with your name and a timestamp, and clears it to be published. The submitter will be notified.
                </p>
              </div>
            </div>
            <div className="px-6 py-5">
              <label className="label">
                Message to submitter <span className="font-normal text-gray-400 dark:text-gray-500">(optional)</span>
              </label>
              <textarea
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
                rows={3}
                placeholder="Add an approval note the submitter will see…"
                className="input"
              />
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
              <button
                onClick={() => setApproveOpen(false)}
                disabled={busy}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmApprove}
                disabled={busy}
                className="btn btn-primary"
              >
                {busy ? 'Processing…' : `Approve ${noun}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deny reason modal */}
      {denyOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setDenyOpen(false); }}
        >
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
            <div className="flex items-start gap-2.5 border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-danger-600 dark:text-danger-400" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">Confirm Denial</h3>
                <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
                  Denying will return this {noun.toLowerCase()} to <strong className="font-medium text-gray-900 dark:text-gray-100">{returnState}</strong> so the
                  author can make corrections and resubmit.
                </p>
              </div>
            </div>
            <div className="px-6 py-5">
              <label className="label">
                Reason for denial <span aria-hidden="true" className="text-danger-600 dark:text-danger-400">*</span>
              </label>
              <textarea
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                rows={4}
                autoFocus
                placeholder="Explain what needs to be corrected before resubmission…"
                className="input"
              />
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
              <button
                onClick={() => setDenyOpen(false)}
                disabled={busy}
                className="btn btn-secondary"
              >
                Back
              </button>
              <button
                onClick={confirmDeny}
                disabled={busy || !denyReason.trim()}
                className="btn btn-danger"
              >
                {busy ? 'Processing…' : `Deny & Return to ${returnState}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReviewActionBar;
